import React, { useState } from "react";
import { centered } from "../styles";
import { Button, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, Spinner, useToastController } from "@fluentui/react-components";
import { Dismiss16Regular } from "@fluentui/react-icons";
import ClientUtil from "../../utils/client-util";
import { RecalculateScope } from "../../utils/common/constants";
import { usePendingRequests } from "../hooks/usePendingRequests";
import { notify } from "./Toast";

const SCOPE_LABELS: Record<RecalculateScope, string> = {
  sheet: "Current sheet",
  workbook: "Whole workbook"
};

export const Recalculate: React.FC = () => {
  const isExcel = localStorage.getItem("client") === "excel";

  const [isRecalculatingFormulas, setIsRecalculatingFormulas] = useState(false);
  const polledPendingRequests = usePendingRequests();
  const pendingRequests = isExcel ? polledPendingRequests : 0;
  const { dispatchToast } = useToastController();

  const isDisabled = isRecalculatingFormulas || pendingRequests > 0;

  async function recalculate(scope?: RecalculateScope) {
    setIsRecalculatingFormulas(true);

    try {
      const result = isExcel ? await ClientUtil.recalculateCells(scope) : await ClientUtil.recalculateCells();
      dispatchToast(notify(result.msg), { intent: "info" });
    } catch (err) {
      console.error(`Error while recalculating formulas ${err}`);
      dispatchToast(notify("Something went wrong while recalculation"), { intent: "error" });
    } finally {
      setIsRecalculatingFormulas(false);
    }
  }

  function cancelPendingRequests() {
    const fetchQueue = (window as any).fetchQueue;
    fetchQueue?.emptyQueue();
  }

  const buttonContent = isRecalculatingFormulas ? (
    <Spinner size="extra-small" label="Scanning for formulas..." />
  ) : pendingRequests > 0 ? (
    <span>Loading… ({pendingRequests})</span>
  ) : (
    <span>Recalculate</span>
  );

  const cancelButton = pendingRequests > 0 && (
    <Button appearance="subtle" size="small" icon={<Dismiss16Regular />} onClick={cancelPendingRequests} aria-label="Cancel pending requests" />
  );

  if (!isExcel) {
    return (
      <div style={{ ...centered, gap: "5px" }}>
        <Button disabled={isDisabled} onClick={() => recalculate()}>
          {buttonContent}
        </Button>
      </div>
    );
  }

  return (
    <div style={{ ...centered, gap: "5px" }}>
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <Button disabled={isDisabled}>{buttonContent}</Button>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem onClick={() => recalculate("sheet")}>{SCOPE_LABELS.sheet}</MenuItem>
            <MenuItem onClick={() => recalculate("workbook")}>{SCOPE_LABELS.workbook}</MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
      {cancelButton}
    </div>
  );
};

export default Recalculate;
